import axios from 'axios';
import {
  SpecialCaseSolvingSuccessResults,
  ConversationMessagesResponse,
  ConversationType,
  FlagId,
  Folder,
  FolderMessageContent,
  FolderMessagesResponse,
  FoldersUnreadMessagesIndicators,
  Message,
  MonitId,
  Priority,
} from '../../models/communication.types';
import env from '../../utils/env';
import { AxiosResponse } from 'axios';
import {
  MessagesFilter,
  MessagesSortById,
  translateMessagesSortByIdToMessagesSortBy,
} from '../../components/Mailbox/MessagesList/MessagesList.types';
import { addParam, addParamsList, downloadFile, compressRecipientIds } from '../../utils/servicesHelper';
import { INotificationTypeId } from '../../components/MessageActions/NotificationType.component';

interface StandardMessagePayload {
  subject: string;
  body: string;
  assessmentSessionId: string;
  priority: Priority;
  url?: string;
  notificationType: INotificationTypeId[];
  recipientBatchId: string;
}

interface StandardMessageRequest {
  assessmentSessionId: string;
  recipientBatchId: string;
  subject: string;
  body: string;
  url: string;
  priorityId: Priority;
  sms: boolean;
  email: boolean;
  push: boolean;
}

interface NotificationFlags {
  sms: boolean;
  email: boolean;
  push: boolean;
}

interface PlainMessageRequest extends NotificationFlags {
  subject: string;
  body: string;
  url: string;
  priorityId: Priority;
}

interface ReplyMessageRequest extends NotificationFlags {
  body: string;
  url: string;
  priorityId: number;
}

interface SpecialCaseMessageRequest extends NotificationFlags {
  specialCaseId: string;
  examTaskSolutionScoreId: string;
  body: string;
  url: string;
}

const unicodeSafeBtoa = (str: string): string => {
  return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16))));
};

const toBase64Url = (base64: string): string => base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

const TRANSPORT_PREFIX = 'b64u_';

const encodeTransportValue = (value?: string): string =>
  value ? `${TRANSPORT_PREFIX}${toBase64Url(unicodeSafeBtoa(value))}` : '';

const encodeSubject = (subject: string): string => encodeTransportValue(subject);

const encodeBody = (body: string): string => encodeTransportValue(body);

const encodeUrl = (url?: string): string => encodeTransportValue(url);

const encodeId = (id?: string): string => encodeTransportValue(id);

const createRecipientBatch = async (recipients: (string | undefined)[]): Promise<string> => {
  const recipientIds = recipients.filter((item): item is string => item !== undefined);
  const compressedBlob = compressRecipientIds(recipientIds);
  const requestBody = new FormData();
  requestBody.append('recipientDeskIdsBinary', compressedBlob, 'recipients.bin');

  const response = await axios({
    method: 'POST',
    url: `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/recipient-batch`,
    headers: { 'Content-Type': 'multipart/form-data' },
    data: requestBody,
  });

  return response.data.recipientBatchId;
};

const toNotificationFlags = (notificationType?: INotificationTypeId[] | null): NotificationFlags => ({
  sms: notificationType?.includes(INotificationTypeId.SMS) ?? false,
  email: notificationType?.includes(INotificationTypeId.EMAIL) ?? false,
  push: notificationType?.includes(INotificationTypeId.PUSH) ?? false,
});

const buildStandardMessagePayload = (attr: StandardMessagePayload): StandardMessageRequest => {
  return {
    assessmentSessionId: attr.assessmentSessionId,
    recipientBatchId: attr.recipientBatchId,
    subject: encodeSubject(attr.subject),
    body: encodeBody(attr.body),
    url: encodeUrl(attr.url),
    priorityId: attr.priority,
    ...toNotificationFlags(attr.notificationType),
  };
};

const appendNotificationFlagsToFormData = (requestBody: FormData, flags: NotificationFlags) => {
  if (flags.email) requestBody.append('Email', 'true');
  if (flags.sms) requestBody.append('Sms', 'true');
  if (flags.push) requestBody.append('Push', 'true');
};

const postMessage = async <TRequest>(
  url: string,
  payload: TRequest,
  file: Blob | undefined,
  buildFormData: (payload: TRequest, attachment: Blob) => FormData,
  params?: Record<string, string | number | boolean>,
) => {
  return file
    ? axios({
      method: 'POST',
      url,
      headers: { 'Content-Type': 'multipart/form-data' },
      data: buildFormData(payload, file),
      params,
    })
    : axios({
      method: 'POST',
      url,
      headers: { 'Content-Type': 'application/json' },
      data: payload,
      params,
    });
};

const putMessage = async <TRequest>(
  url: string,
  payload: TRequest,
  file: Blob | undefined,
  buildFormData: (payload: TRequest, attachment: Blob) => FormData,
  params?: Record<string, string | number | boolean>,
) => {
  return file
    ? axios({
      method: 'PUT',
      url,
      headers: { 'Content-Type': 'multipart/form-data' },
      data: buildFormData(payload, file),
      params,
    })
    : axios({
      method: 'PUT',
      url,
      headers: { 'Content-Type': 'application/json' },
      data: payload,
      params,
    });
};

const buildStandardMessageWithAttachmentPayload = (attr: StandardMessageRequest, file: Blob): FormData => {
  const requestBody = new FormData();
  requestBody.append('AssessmentSessionId', attr.assessmentSessionId.toString());
  requestBody.append('RecipientBatchId', attr.recipientBatchId);
  requestBody.append('Subject', attr.subject);
  requestBody.append('Body', attr.body);
  requestBody.append('Url', attr.url);
  requestBody.append('PriorityId', attr.priorityId.toString());
  requestBody.append('Attachment', file);
  appendNotificationFlagsToFormData(requestBody, attr);
  return requestBody;
};

const buildReplyMessagePayload = (
  body: string,
  priority: number,
  url: string | undefined,
  notificationType: INotificationTypeId[],
): ReplyMessageRequest => ({
  body: encodeBody(body),
  url: encodeUrl(url),
  priorityId: priority,
  ...toNotificationFlags(notificationType),
});

const appendReplyPayloadToFormData = (payload: ReturnType<typeof buildReplyMessagePayload>, file: Blob): FormData => {
  const requestBody = new FormData();
  requestBody.append('Body', payload.body);
  requestBody.append('Url', payload.url);
  requestBody.append('PriorityId', payload.priorityId.toString());
  requestBody.append('Attachment', file);
  appendNotificationFlagsToFormData(requestBody, payload);
  return requestBody;
};

const buildForwardMessagePayload = (
  body: string,
  priority: number,
  recipientBatchId: string,
  notificationType: INotificationTypeId[],
) => ({
  body: encodeBody(body),
  priorityId: priority,
  recipientBatchId,
  ...toNotificationFlags(notificationType),
});

const buildPlainMessagePayload = (
  subject: string,
  body: string,
  priority: Priority,
  url: string | undefined,
  notificationType?: INotificationTypeId[] | null,
): PlainMessageRequest => ({
  subject: encodeSubject(subject),
  body: encodeBody(body),
  url: encodeUrl(url),
  priorityId: priority,
  ...toNotificationFlags(notificationType),
});

const appendPlainMessagePayloadToFormData = (
  payload: ReturnType<typeof buildPlainMessagePayload>,
  file: Blob,
  extraFields?: Record<string, string | number | boolean | string[] | number[] | undefined>,
): FormData => {
  const requestBody = new FormData();
  requestBody.append('Subject', payload.subject);
  requestBody.append('Body', payload.body);
  requestBody.append('Url', payload.url);
  requestBody.append('PriorityId', payload.priorityId.toString());
  requestBody.append('Attachment', file);
  appendNotificationFlagsToFormData(requestBody, payload);
  Object.entries(extraFields ?? {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      value.forEach((v) => requestBody.append(key, v.toString()));
    } else {
      requestBody.append(key, value.toString());
    }
  });
  return requestBody;
};

const splitCommaSeparatedNumbers = (value?: string): number[] | undefined => {
  if (!value) return undefined;
  const items = value
    .split(',')
    .map((item) => Number(item.trim()))
    .filter((item) => !Number.isNaN(item));
  return items.length > 0 ? items : undefined;
};

const mapAccountStatus = (accountStatus?: string): boolean | undefined => {
  if (!accountStatus || accountStatus === 'Aktywne,Nieaktywne') {
    return undefined;
  }

  return accountStatus === 'Aktywne';
};

const mapOnlineStatus = (status?: string): boolean | undefined => {
  if (!status || !['online', 'offline'].includes(status)) {
    return undefined;
  }

  return status === 'online';
};

const buildAdminFilterPayload = (
  subject: string,
  body: string,
  priority: Priority,
  url: string | undefined,
  notificationType: INotificationTypeId[],
  firstName?: string,
  lastName?: string,
  email?: string,
  pesel?: string,
  phone?: string,
  accountTypeFilter?: string,
  examCommitteeFilter?: string,
  accountStatus?: string,
  status?: string,
) => ({
  subject: encodeSubject(subject),
  body: encodeBody(body),
  url: encodeUrl(url),
  priorityId: priority,
  firstName,
  lastName,
  email,
  pesel,
  phone,
  accountTypes: splitCommaSeparatedNumbers(accountTypeFilter),
  examCommittees: splitCommaSeparatedNumbers(examCommitteeFilter),
  emailConfirmed: mapAccountStatus(accountStatus),
  isOnline: mapOnlineStatus(status),
  smsNotification: notificationType.includes(INotificationTypeId.SMS),
  emailNotification: notificationType.includes(INotificationTypeId.EMAIL),
  pushNotification: notificationType.includes(INotificationTypeId.PUSH),
});

const appendAdminFilterPayloadToFormData = (
  payload: ReturnType<typeof buildAdminFilterPayload>,
  file: Blob,
): FormData => {
  const requestBody = new FormData();
  requestBody.append('Subject', payload.subject);
  requestBody.append('Body', payload.body);
  requestBody.append('Url', payload.url);
  requestBody.append('PriorityId', payload.priorityId.toString());
  requestBody.append('Attachment', file);
  if (payload.firstName) requestBody.append('FirstName', payload.firstName);
  if (payload.lastName) requestBody.append('LastName', payload.lastName);
  if (payload.email) requestBody.append('Email', payload.email);
  if (payload.pesel) requestBody.append('Pesel', payload.pesel);
  if (payload.phone) requestBody.append('Phone', payload.phone);
  payload.accountTypes?.forEach((item) => requestBody.append('AccountTypes', item.toString()));
  payload.examCommittees?.forEach((item) => requestBody.append('ExamCommittees', item.toString()));
  if (payload.emailConfirmed !== undefined) requestBody.append('EmailConfirmed', payload.emailConfirmed.toString());
  if (payload.isOnline !== undefined) requestBody.append('IsOnline', payload.isOnline.toString());
  requestBody.append('SmsNotification', String(payload.smsNotification));
  requestBody.append('EmailNotification', String(payload.emailNotification));
  requestBody.append('PushNotification', String(payload.pushNotification));
  return requestBody;
};

const buildSpecialCasePayload = (
  body: string,
  examTaskSolutionScoreId: string | undefined,
  specialCaseId: string,
  url?: string,
  notificationType?: INotificationTypeId[] | null,
): SpecialCaseMessageRequest => ({
  specialCaseId,
  examTaskSolutionScoreId: examTaskSolutionScoreId ?? '',
  body: encodeBody(body),
  url: encodeUrl(url),
  ...toNotificationFlags(notificationType),
});

const appendSpecialCasePayloadToFormData = (payload: SpecialCaseMessageRequest, file: Blob): FormData => {
  const requestBody = new FormData();
  requestBody.append('SpecialCaseId', payload.specialCaseId.toString());
  requestBody.append('ExamTaskSolutionScoreId', payload.examTaskSolutionScoreId.toString());
  requestBody.append('Body', payload.body);
  requestBody.append('Url', payload.url);
  requestBody.append('Attachment', file);
  appendNotificationFlagsToFormData(requestBody, payload);
  return requestBody;
};

export const sendMessage = async (
  subject: string,
  body: string,
  assessmentSessionId: string | undefined,
  priority: Priority,
  file: Blob | undefined,
  url: string | undefined,
  recipients: (string | undefined)[],
  notificationType: INotificationTypeId[],
  shouldGetMessageInfo = false,
  functionForZwo = false,
): Promise<Message> => {
  const recipientBatchId = await createRecipientBatch(recipients);
  const payload = buildStandardMessagePayload({
    subject,
    body,
    assessmentSessionId: assessmentSessionId || '',
    priority,
    url,
    notificationType,
    recipientBatchId,
  });

  console.log(payload);

  const response = await postMessage(
    file
      ? `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/standard/attachment`
      : `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/standard`,
    payload,
    file,
    buildStandardMessageWithAttachmentPayload,
    { shouldGetMessageInfo, functionForZwo },
  );

  return response.data;
};

export const sendMessageAdmin = async (
  subject: string,
  body: string,
  priority: Priority,
  file: Blob | undefined,
  url: string | undefined,
  recipients: string[] | (string | undefined)[],
  notificationType: INotificationTypeId[],
  shouldGetMessageInfo = false,
): Promise<Message> => {
  const recipientBatchId = await createRecipientBatch(recipients);
  const payload = {
    ...buildPlainMessagePayload(subject, body, priority, url, notificationType),
    recipientBatchId,
  };

  const response = await postMessage(
    file
      ? `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/admin/plain/attachment`
      : `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/admin/plain`,
    payload,
    file,
    (messagePayload, attachment) =>
      appendPlainMessagePayloadToFormData(messagePayload, attachment, { RecipientBatchId: recipientBatchId }),
    shouldGetMessageInfo ? { shouldGetMessageInfo: true } : undefined,
  );
  return response.data;
};

export const sendMessageAdminToAllChecked = async (
  subject: string,
  body: string,
  priority: Priority,
  file: Blob | undefined,
  url: string | undefined,
  notificationType: INotificationTypeId[],
  firstName?: string,
  lastName?: string,
  email?: string,
  pesel?: string,
  phone?: string,
  accountTypeFilter?: string,
  examCommitteeFilter?: string,
  accountStatus?: string,
  status?: string,
  shouldGetMessageInfo = false,
): Promise<Message> => {
  const payload = buildAdminFilterPayload(
    subject,
    body,
    priority,
    url,
    notificationType,
    firstName,
    lastName,
    email,
    pesel,
    phone,
    accountTypeFilter,
    examCommitteeFilter,
    accountStatus,
    status,
  );

  const response = await postMessage(
    file
      ? `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/admin/query/plain/attachment`
      : `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/admin/query/plain`,
    payload,
    file,
    appendAdminFilterPayloadToFormData,
    shouldGetMessageInfo ? { shouldGetMessageInfo: true } : undefined,
  );
  return response.data;
};

export const sendReply = async (
  messageId: string,
  body: string,
  priority: number,
  file: Blob | undefined,
  url: string | undefined,
  notificationType: INotificationTypeId[],
  shouldGetMessageInfo = false,
): Promise<Message> => {
  const payload = buildReplyMessagePayload(body, priority, url, notificationType);
  const response = await putMessage(
    file
      ? `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/${messageId}/plain/attachment`
      : `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/${messageId}/plain`,
    payload,
    file,
    appendReplyPayloadToFormData,
    shouldGetMessageInfo ? { shouldGetMessageInfo: true } : undefined,
  );
  return response.data;
};

export const sendForward = async (
  messageId: string,
  body: string,
  priority: number,
  recipients: string[],
  notificationType: INotificationTypeId[],
  shouldGetMessageInfo = false,
): Promise<Message> => {
  const recipientBatchId = await createRecipientBatch(recipients);
  const payload = buildForwardMessagePayload(body, priority, recipientBatchId, notificationType);
  const response = await axios({
    method: 'POST',
    url: `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/${messageId}/forward/plain`,
    headers: { 'Content-Type': 'application/json' },
    data: payload,
    params: shouldGetMessageInfo
      ? {
        shouldGetMessageInfo: true,
      }
      : undefined,
  });
  return response.data;
};

export const sendMessageHelpdesk = async (
  subject: string,
  body: string,
  examTasksGroupId: string | undefined,
  priority: Priority,
  file: Blob | undefined,
  url: string | undefined,
  shouldGetMessageInfo = false,
): Promise<Message> => {
  const payload = {
    ...buildPlainMessagePayload(subject, body, priority, url),
    examTasksGroupId,
  };

  const response = await postMessage(
    file
      ? `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/helpdesk/plain/attachment`
      : `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/helpdesk/plain`,
    payload,
    file,
    (messagePayload, attachment) =>
      appendPlainMessagePayloadToFormData(messagePayload, attachment, { ExamTasksGroupId: examTasksGroupId }),
    shouldGetMessageInfo ? { shouldGetMessageInfo: true } : undefined,
  );
  return response.data;
};

export const sendMessageSpecialCase = async (
  body: string,
  examTaskSolutionScoreId: string | undefined,
  specialCaseId: string,
  file?: Blob | null,
  url?: string,
  notificationType?: INotificationTypeId[] | null,
): Promise<number> => {
  const payload = buildSpecialCasePayload(body, examTaskSolutionScoreId, specialCaseId, url, notificationType);
  const response = await putMessage(
    file
      ? `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/specialCase/plain/attachment`
      : `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/specialCase/plain`,
    payload,
    file ?? undefined,
    appendSpecialCasePayloadToFormData,
  );
  return response.data;
};

export const sendMessageExamTaskGroupSolution = async (
  body: string,
  subject: string,
  priority: Priority,
  examTaskGroupSolutionScoreId: string | undefined,
  file?: Blob | null,
  url?: string,
  notificationType?: INotificationTypeId[] | null,
  examinerIds?: string[],
): Promise<Message> => {
  const payload = {
    ...buildPlainMessagePayload(subject, body, priority, url, notificationType),
    examTaskGroupSolutionScoreId: examTaskGroupSolutionScoreId ?? '',
    examinerIds,
  };

  const response = await putMessage(
    file
      ? `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/examTaskGroupSolution/plain/attachment`
      : `${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/examTaskGroupSolution/plain`,
    payload,
    file ?? undefined,
    (messagePayload, attachment) =>
      appendPlainMessagePayloadToFormData(messagePayload, attachment, {
        ExamTaskGroupSolutionScoreId: messagePayload.examTaskGroupSolutionScoreId,
        ExaminerIds: messagePayload.examinerIds,
      }),
  );
  return response.data;
};

export function getFolders(): Promise<AxiosResponse<FoldersUnreadMessagesIndicators>> {
  const response = axios.get(env.REACT_APP_COMMUNICATION__FOLDER_URL);
  return response;
}

const collectGetMessagesSortingParams = (
  sortByColumnId: MessagesSortById | undefined,
  isDescending: boolean,
): string => {
  let params = '';
  if (sortByColumnId !== undefined) {
    params += addParam('sortBy', translateMessagesSortByIdToMessagesSortBy(sortByColumnId));
  }
  params += addParam('isDescending', isDescending ? 'true' : 'false');
  return params;
};

const collectGetMessagesParams = (
  filter: MessagesFilter,
  sortByColumnId: MessagesSortById | undefined,
  isDescending: boolean,
): string => {
  let params = '';
  params += addParam('assessmentSession', filter.assessmentSession);
  params += addParam('sender', filter.sender);
  params += addParam('recipient', filter.recipient);
  params += addParam('subject', filter.subject);
  params += addParam('sentDateFrom', filter.sentDateFrom);
  params += addParam('sentDateTo', filter.sentDateTo);
  params += addParamsList('flag', filter.flag);
  params += addParam('hasAttachment', filter.monit.includes(MonitId.hasAttachment) ? 'true' : '');
  params += addParam('hasLink', filter.monit.includes(MonitId.hasLink) ? 'true' : '');
  params += addParam('isRead', filter.isUnread ? 'false' : '');

  const priorities: Priority[] = [];
  if (filter.monit.includes(MonitId.highPriority)) {
    priorities.push(Priority.High);
  }
  if (filter.monit.includes(MonitId.mediumPriority)) {
    priorities.push(Priority.Medium);
  }
  params += addParamsList('priority', priorities);
  params += collectGetMessagesSortingParams(sortByColumnId, isDescending);
  return params;
};

export function getFolderMessages(
  folder: Folder,
  filter: MessagesFilter,
  sortByColumnId: MessagesSortById | undefined,
  isDescending: boolean,
  pageNumber: number,
  pageSize: number,
  includeFolderSummary: boolean,
  signal?: AbortSignal,
): Promise<AxiosResponse<FolderMessagesResponse>> {
  const params = collectGetMessagesParams(filter, sortByColumnId, isDescending);
  const response = axios.get(
    `${
      env.REACT_APP_COMMUNICATION__FOLDER_URL
    }/${folder}/message?page=${pageNumber}&pageSize=${pageSize}${params}&includeFolderSummary=${
      includeFolderSummary ? 'true' : 'false'
    }`,
    {
      signal: signal ?? undefined,
    },
  );
  return response;
}

export function getFolderMessagesSearchResults(
  folder: Folder,
  searchPhrase: string,
  sortByColumnId: MessagesSortById | undefined,
  isDescending: boolean,
  pageNumber: number,
  pageSize: number,
  includeFolderSummary: boolean,
  signal?: AbortSignal,
): Promise<AxiosResponse<FolderMessagesResponse>> {
  const params = collectGetMessagesSortingParams(sortByColumnId, isDescending);
  const response = axios.get(
    `${
      env.REACT_APP_COMMUNICATION__FOLDER_URL
    }/${folder}/message/search?page=${pageNumber}&pageSize=${pageSize}${params}&search=${searchPhrase}&includeFolderSummary=${
      includeFolderSummary ? 'true' : 'false'
    }`,
    {
      signal: signal ?? undefined,
    },
  );
  return response;
}

export function getConversationMessages(
  folder: Folder,
  conversationId: string,
  filter: MessagesFilter,
  sortByColumnId: MessagesSortById | undefined,
  isDescending: boolean,
  pageNumber: number,
  pageSize: number,
): Promise<AxiosResponse<ConversationMessagesResponse>> {
  const params = collectGetMessagesParams(filter, sortByColumnId, isDescending);
  const response = axios.get(
    `${env.REACT_APP_COMMUNICATION__FOLDER_URL}/${folder}/conversation/${conversationId}/message?page=${pageNumber}&pageSize=${pageSize}${params}`,
  );
  return response;
}

export function getMessageContent(messageId: string): Promise<AxiosResponse<FolderMessageContent>> {
  const response = axios.get(`${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/${messageId}`);
  return response;
}

export const hasUnreadMessages = (priority: Priority): Promise<AxiosResponse<ConversationType>> => {
  const response = axios.get(`${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/hasUnreadMessages?priorityId=${priority}`);
  return response;
};

export const deleteMessage = (messageId: string): Promise<AxiosResponse> => {
  const response = axios.delete(`${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/${messageId}`);
  return response;
};

export const setMessageFlag = (messageId: string, flagId: FlagId): Promise<AxiosResponse> => {
  const response = axios.put(`${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/${messageId}/flag/${flagId}`);
  return response;
};

export const removeMessageFlag = (messageId: string): Promise<AxiosResponse> => {
  const response = axios.delete(`${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/${messageId}/flag`);
  return response;
};

export function getAttachment(messageId: string, fileName: string): void {
  downloadFile(`${env.REACT_APP_COMMUNICATION__MESSAGE_URL}/${messageId}/Attachment`, fileName);
}

export const closeSpecialCase = (conversationId: string): Promise<AxiosResponse<SpecialCaseSolvingSuccessResults>> => {
  const response = axios.patch(
    `${env.REACT_APP_COMMUNICATION__CONVERSATION_URL}/conversationId/${conversationId}/closeSpecialCase`,
  );
  return response;
};

export const closeHelpdesk = (conversationId: string): Promise<AxiosResponse> => {
  const response = axios.patch(
    `${env.REACT_APP_COMMUNICATION__CONVERSATION_URL}/conversationId/${conversationId}/closeHelpdeskRequest`,
  );
  return response;
};
